import { useMemo, useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/design-system/components/react";
import { useFindingSlices } from "@/hooks/useFindingSlices";
import type { GalleryFinding } from "@/hooks/useStudyQueries";
import type { TriageFlag, TriageReview } from "@/types/triageReview";
import { FaChevronLeft, FaChevronRight } from "@/utils/icons";

function HideButton({ onCollapse }: { onCollapse: () => void }) {
  return (
    <button
      type="button"
      onClick={onCollapse}
      aria-label="Hide triage panel"
      title="Hide triage panel (.)"
      className="inline-flex shrink-0 items-center gap-1 rounded-xs border border-border px-1.5 py-0.5 text-[11px] text-fg-muted hover:border-border-strong hover:text-fg"
    >
      Hide
      <FaChevronRight className="h-2.5 w-2.5" />
    </button>
  );
}

/** The device's key image for one flagged finding (`name`), as a clean-pixels
 * preview (no marks, no boxes), with the device's caption (if it has one) under
 * it and, when `named`, the finding's name above the caption. Clicking it
 * enlarges the same image in a dialog; no viewer is ever driven from here (the
 * key image is informational, not a navigation aid). */
export function KeyImagePreview({
  name,
  frame,
  loading,
  caption,
  named = false,
  className,
}: {
  name: string;
  frame?: string;
  loading: boolean;
  caption: string | null;
  named?: boolean;
  className?: string;
}) {
  const [enlarged, setEnlarged] = useState(false);
  return (
    <figure className={className}>
      {frame ? (
        <button
          type="button"
          onClick={() => setEnlarged(true)}
          aria-label={`Enlarge key image: ${name}`}
          title="Enlarge (the viewer does not move)"
          className="block aspect-square w-full cursor-zoom-in overflow-hidden rounded-sharp border border-border bg-black hover:border-border-strong"
        >
          <img src={frame} alt="Key image" className="h-full w-full object-contain" />
        </button>
      ) : (
        <div className="flex aspect-square items-center justify-center overflow-hidden rounded-sharp border border-border bg-black text-[10px] text-fg-faint">
          {loading ? "Loading key image…" : "Key image unavailable"}
        </div>
      )}
      <figcaption className="mt-1.5 grid gap-0.5">
        {named && <span className="text-sm font-medium text-fg">{name}</span>}
        {caption && <span className="text-[10px] text-fg-dim">{caption}</span>}
      </figcaption>
      {frame && (
        <Dialog open={enlarged} onOpenChange={setEnlarged}>
          <DialogContent
            className="max-w-[min(90vw,90vh)] gap-3 border-border-strong bg-surface p-4"
            // Keys stay in the dialog: the page's hotkeys (a viewer's scroll,
            // presets and Escape; a notification's case steps) must not act
            // behind it.
            onKeyDown={(event) => event.stopPropagation()}
          >
            <DialogTitle className="pr-8 text-sm font-medium text-fg">{name}</DialogTitle>
            <div className="overflow-hidden rounded-sharp border border-border bg-black">
              <img
                src={frame}
                alt="Key image, enlarged"
                className="max-h-[75vh] w-full object-contain"
              />
            </div>
            {caption && (
              <DialogDescription className="text-[11px] text-fg-dim">{caption}</DialogDescription>
            )}
          </DialogContent>
        </Dialog>
      )}
    </figure>
  );
}

/** Loads every flag's key image once (switching findings is then instant) and
 * shows the selected flag's. */
function SelectedKeyImage({
  studyId,
  flagged,
  selected,
  caption,
}: {
  studyId: string;
  flagged: TriageFlag[];
  selected: TriageFlag;
  caption: string;
}) {
  const findings = useMemo<GalleryFinding[]>(
    () =>
      flagged
        .filter((flag) => flag.top_slices.length > 0)
        .map((flag) => ({
          condition: flag.condition,
          category: null,
          topSlice: flag.top_slices[0],
          advancement: null,
          percentile: null,
        })),
    [flagged],
  );
  const slices = useFindingSlices(studyId, "", findings);
  const hasKeySlice = selected.top_slices.length > 0;
  const state = hasKeySlice ? slices[selected.condition] : undefined;
  return (
    <KeyImagePreview
      key={selected.condition}
      name={selected.display_name}
      className="px-3 pb-3"
      frame={state?.keyFrame}
      loading={hasKeySlice && state?.loading !== false}
      caption={caption}
    />
  );
}

/** Flagged findings as a selectable list; the preview follows the selection. */
function FlaggedFindings({
  flagged,
  studyId,
  caption,
}: {
  flagged: TriageFlag[];
  studyId?: string;
  caption: string;
}) {
  const firstWithImage = flagged.find((flag) => flag.top_slices.length > 0) ?? flagged[0];
  const [picked, setPicked] = useState(firstWithImage.condition);
  const selected = flagged.find((flag) => flag.condition === picked) ?? firstWithImage;
  return (
    <>
      <div className="px-3 pt-3 font-mono text-[10px] uppercase tracking-[0.14em] text-fg-dim">
        Flagged · {flagged.length}
      </div>
      <ul aria-label="Flagged findings" className="p-3 space-y-2">
        {flagged.map((flag) => {
          const isSelected = flag.condition === selected.condition;
          return (
            <li key={flag.condition}>
              <button
                type="button"
                aria-pressed={isSelected}
                onClick={() => setPicked(flag.condition)}
                className={`flex w-full items-center justify-between gap-2 border border-border-strong bg-surface-2 px-2 py-1.5 text-left text-sm font-medium border-l-2 ${
                  isSelected ? "border-l-signal" : "hover:border-l-fg-muted"
                }`}
              >
                <span>{flag.display_name}</span>
                {studyId && (
                  <span className="shrink-0 font-mono text-[9px] uppercase tracking-widest text-fg-dim">
                    {isSelected ? "Showing" : "Preview"}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {studyId && (
        <SelectedKeyImage
          studyId={studyId}
          flagged={flagged}
          selected={selected}
          caption={caption}
        />
      )}
    </>
  );
}

/** The released triage device's notification for one study: its flagged findings
 * as one device verdict, and the device's unmarked key image per flag. No scores
 * or operating points; nothing drawn on the image. */
export function TriageNotificationPanel({
  result,
  loading,
  error,
  studyId,
  ageRecorded = true,
  onCollapse,
}: {
  result?: TriageReview;
  loading: boolean;
  error: boolean;
  /** Study id used to load the key images; omit to show no image. */
  studyId?: string;
  /** False when the study had no recorded age (routed anyway, Pranav 2026-10-06). */
  ageRecorded?: boolean;
  /** Collapses the panel to its rail; omit to show no Hide control. */
  onCollapse?: () => void;
}) {
  const bare = (node: ReactNode) => (
    <div className="h-full bg-bg text-fg">
      {onCollapse && (
        <div className="flex justify-end p-2">
          <HideButton onCollapse={onCollapse} />
        </div>
      )}
      {node}
    </div>
  );
  if (error)
    return bare(
      <p role="alert" className="p-4">
        Could not load the triage result.
      </p>,
    );
  if (loading)
    return bare(
      <p role="status" className="p-4">
        Loading triage result…
      </p>,
    );
  if (!result)
    return bare(
      <p role="alert" className="p-4">
        Triage result is unavailable.
      </p>,
    );
  const flagged = result.flagged ?? [];
  return (
    <section aria-label="Triage notification" className="h-full overflow-y-auto bg-bg text-fg">
      <header className="flex items-start justify-between gap-2 p-3 border-b border-border">
        <div className="min-w-0">
          <h2 className="font-semibold">Triage notification</h2>
          <p className="mt-1 text-xs text-fg-muted font-mono">
            {result.device} v{result.version}
          </p>
          {!ageRecorded && <p className="mt-1 text-xs text-fg-muted">Age not recorded</p>}
        </div>
        {onCollapse && <HideButton onCollapse={onCollapse} />}
      </header>
      {!result.successful_inference ? (
        <p className="p-3 text-sm">
          Not analyzed by the device{result.message ? `: ${result.message}` : "."}
        </p>
      ) : flagged.length === 0 ? (
        <p className="p-3 text-sm">No flag</p>
      ) : (
        <FlaggedFindings
          key={flagged.map((flag) => flag.condition).join("|")}
          flagged={flagged}
          studyId={studyId}
          caption={result.labeling.key_image_caption}
        />
      )}
      <p className="p-3 text-xs text-fg-muted border-t border-border">
        {result.labeling.clinician_note}
      </p>
    </section>
  );
}

/** A worklist study that routing kept away from the device: no device output exists
 * for the reader, only the routing reason. */
export function TriageNotRoutedPanel({
  device,
  reason,
  onCollapse,
}: {
  device: string;
  reason: string;
  onCollapse?: () => void;
}) {
  return (
    <section aria-label="Not routed" className="h-full overflow-y-auto bg-bg text-fg">
      <header className="flex items-start justify-between gap-2 p-3 border-b border-border">
        <div className="min-w-0">
          <h2 className="font-semibold">Not routed to {device}</h2>
          <p className="mt-1 text-sm text-fg-muted">{reason}</p>
        </div>
        {onCollapse && <HideButton onCollapse={onCollapse} />}
      </header>
      <p className="p-3 text-xs text-fg-muted">
        This study is outside the device's indications, so the device was not applied. Read it per
        the standard of care.
      </p>
    </section>
  );
}

/** What the collapsed rail shows: the flag count, "0" for no flag, "–" when the
 * device gave no result to count (not routed, not analyzed, loading, unavailable). */
export function triageRailCount(result: TriageReview | undefined, routed: boolean): string {
  if (!routed || !result || !result.successful_inference || !result.flagged) return "–";
  return String(result.flagged.length);
}

/** The triage panel collapsed to a narrow rail: the flag count stays visible. */
export function TriageCollapsedRail({ count, onExpand }: { count: string; onExpand: () => void }) {
  const flagged = count !== "–" && count !== "0";
  const label =
    count === "–"
      ? "Show triage panel (no device result)"
      : flagged
        ? `Show triage panel (${count} flagged)`
        : "Show triage panel (no flag)";
  return (
    <div
      data-testid="triage-rail"
      className="flex h-full w-9 shrink-0 flex-col items-center border-l border-border bg-bg pt-2"
    >
      <button
        type="button"
        onClick={onExpand}
        aria-label={label}
        title={`${label} (.)`}
        className="flex flex-col items-center gap-1.5 rounded-xs px-1 py-1.5 text-fg-muted hover:bg-surface-2 hover:text-fg"
      >
        <FaChevronLeft className="h-3 w-3" />
        <span
          data-testid="triage-rail-count"
          className={`min-w-[1.25rem] rounded-xs border px-1 py-px text-center font-mono text-[11px] tabular-nums ${
            flagged
              ? "border-border-strong bg-surface-2 font-bold text-fg"
              : "border-border text-fg-muted"
          }`}
        >
          {count}
        </span>
        <span className="font-mono text-[9px] uppercase tracking-widest [writing-mode:vertical-rl]">
          Triage
        </span>
      </button>
    </div>
  );
}
